import { ListItem, ListItemIcon, ListItemText } from '@mui/material';
import classNames from 'classnames';
import React, { ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BACKGROUND_ACTIVE,
  BACKGROUND_HOVER,
  BADGE_NEW,
  BORDER_DIVIDER,
  DRAWER_INACTIVE_SELECTION,
  GRAY_BACKGROUND,
  WHITE,
} from '~/Constants/Colors.constants';
import { FONT_SIZE_SMALL, FONT_SIZE_SMALLISH } from '~/Constants/UI.constants';
import { Divider, Typography } from '~/UIKit';
import { makeStyles } from '~/UIKit/Style';

interface DrawerItemProps {
  icon: ReactNode;
  text: string;
  textClass?: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  secondaryIcon?: ReactNode;
  borderTop?: boolean;
  testId?: string;
  isNew?: boolean;
  isActive?: boolean;
  isSubMenu?: boolean;
  isHovered?: boolean; // Add this prop
}

const useStyles = makeStyles({
  container: {
    borderLeft: `2px solid transparent`,
  },
  button: {
    '&:hover, &.hovered': {
      // Add .hovered class
      backgroundColor: BACKGROUND_HOVER,
      '& .MuiListItemIcon-root, & .MuiListItemText-root, & .MuiListItemSecondaryAction-root': {
        color: GRAY_BACKGROUND,
      },
    },
  },
  text: {
    letterSpacing: 0.5,
    fontSize: FONT_SIZE_SMALLISH,
  },
  iconRoot: {
    display: 'flex',
    paddingRight: 15,
    minWidth: 40,
  },
  badgeNew: {
    color: WHITE,
    lineHeight: 1.2,
    borderRadius: 3,
    padding: '0 8px',
    fontWeight: 'bold',
    fontSize: FONT_SIZE_SMALL,
    textTransform: 'uppercase',
    background: BADGE_NEW,
  },
  active: {
    color: GRAY_BACKGROUND,
    backgroundColor: BACKGROUND_ACTIVE,
    fontWeight: 'bold',
  },
  subMenu: {
    borderLeft: `2px solid ${BORDER_DIVIDER}`,
  },
  activeSubMenu: {
    fontWeight: 'bold',
    borderLeft: `2px solid ${DRAWER_INACTIVE_SELECTION}`,
  },
});

export const DrawerItem = ({
  icon,
  text,
  onClick,
  secondaryIcon,
  borderTop,
  textClass,
  testId = '',
  isNew = false,
  isActive,
  isSubMenu,
  isHovered,
}: DrawerItemProps) => {
  const drawerItemRef = useRef(null);
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <div
      className={classNames(
        {
          [classes.active]: isActive,
          [classes.subMenu]: isSubMenu,
          [classes.activeSubMenu]: isSubMenu && isActive,
        },
        classes.container,
      )}
    >
      {borderTop && <Divider />}
      <ListItem
        button
        classes={{ button: classes.button }}
        className={classNames({ hovered: isHovered })}
        onClick={onClick}
        data-testid={testId}
        ref={drawerItemRef}
      >
        {icon && (
          <ListItemIcon
            classes={{ root: classes.iconRoot }}
            className={classNames({ [classes.activeIcon]: isActive })}
          >
            {icon}
          </ListItemIcon>
        )}
        <ListItemText
          primary={<span className={classNames(classes.text, textClass)}>{text}</span>}
          disableTypography
        />
        {isNew && (
          <Typography variant="body2" className={classes.badgeNew}>
            {t('badge.new')}
          </Typography>
        )}
        {secondaryIcon}
      </ListItem>
    </div>
  );
};
